import { useCallback, useRef, useState } from "react";

/**
 * Marquee (rubber-band) multi-select for the placed dashboard grid.
 *
 * Pressing the primary button on a section grid's EMPTY space (not on a card
 * — cards own their click-to-toggle and HTML5 drag gestures) and dragging
 * past a small threshold draws a selection rectangle over that section. While
 * the pointer moves, every card whose bounding box intersects the rectangle
 * is selected (live-updating); releasing keeps the selection, and the
 * existing group-drag behavior then moves it as one.
 *
 * The gesture is SECTION-SCOPED: the rectangle belongs to the section it
 * started in and only that section's cards are considered (each section is an
 * independent canvas — matching the rest of the placed-layout interactions).
 *
 * Built on pointer events (with pointer capture once the threshold is
 * passed), NOT HTML5 drag and drop, so it can't collide with the card /
 * section drag sources:
 *
 * - a press below the threshold is a plain click — untouched, so the
 *   "first click on empty space clears the selection" behavior (and the
 *   empty-cell "+" add flow) keeps working;
 * - pointer capture is taken only when the marquee ACTIVATES, so plain
 *   clicks keep native event targeting;
 * - the click the browser fires after a completed marquee is flagged via
 *   {@link MarqueeSelect.consumeMarqueeEndClick} so the document-level
 *   "clear selection on outside click" listener can ignore it.
 *
 * The hook owns only the transient gesture state; the selection itself stays
 * with the caller via `onMarqueeChange` (same ownership split as
 * `usePlacedReorder`).
 */

/** An axis-aligned rectangle in a shared coordinate space. */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Pointer travel (in px) below which a press stays a plain click. */
export const MARQUEE_THRESHOLD_PX = 4;

/** The rectangle spanned by two corner points, in the points' space. */
export function rectFromPoints(
  a: { x: number; y: number },
  b: { x: number; y: number },
): Rect {
  return {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** True when the rectangles overlap. Merely touching along a shared edge or
 *  corner doesn't count, but a degenerate zero-width/height marquee (a
 *  perfectly straight drag) crossing THROUGH a rect does. */
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return (
    a.left < b.left + b.width &&
    b.left < a.left + a.width &&
    a.top < b.top + b.height &&
    b.top < a.top + a.height
  );
}

/** `rect` clipped to `bounds` (both in the same space). Collapses to a
 *  zero-size rect pinned inside `bounds` when they don't overlap. */
export function clampRectTo(rect: Rect, bounds: Rect): Rect {
  const left = Math.min(
    Math.max(rect.left, bounds.left),
    bounds.left + bounds.width,
  );
  const top = Math.min(
    Math.max(rect.top, bounds.top),
    bounds.top + bounds.height,
  );
  const right = Math.min(
    Math.max(rect.left + rect.width, bounds.left),
    bounds.left + bounds.width,
  );
  const bottom = Math.min(
    Math.max(rect.top + rect.height, bounds.top),
    bounds.top + bounds.height,
  );
  return { left, top, width: right - left, height: bottom - top };
}

/** True once the pointer has travelled at least `threshold` px from `a`. */
export function exceedsThreshold(
  a: { x: number; y: number },
  b: { x: number; y: number },
  threshold: number = MARQUEE_THRESHOLD_PX,
): boolean {
  return Math.hypot(b.x - a.x, b.y - a.y) >= threshold;
}

/** ids of the entries whose rects intersect `rect`. */
export function intersectingIds(
  rect: Rect,
  entries: readonly { id: string; rect: Rect }[],
): Set<string> {
  const ids = new Set<string>();
  for (const e of entries) {
    if (rectsIntersect(rect, e.rect)) {
      ids.add(e.id);
    }
  }
  return ids;
}

/** The rectangle of an active marquee, ready to render. */
export interface ActiveMarquee {
  /** Section the gesture started in — only its grid draws the rectangle. */
  sectionId: string;
  /** Rectangle in the section grid's local coordinates (clipped to it). */
  rect: Rect;
}

export interface MarqueeGridHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface MarqueeSelect {
  /** The rectangle to draw, or null while no marquee is active. */
  marquee: ActiveMarquee | null;
  /** Props for one section's grid element. */
  gridHandlers: (sectionId: string) => MarqueeGridHandlers;
  /** True exactly once for the click event the browser fires right after a
   *  completed marquee — callers use it to keep that click from clearing the
   *  selection the marquee just made. */
  consumeMarqueeEndClick: () => boolean;
}

interface PendingGesture {
  sectionId: string;
  pointerId: number;
  grid: HTMLElement;
  /** Anchor corner in CLIENT coordinates as of pointerdown. */
  start: { x: number; y: number };
  /** Window scroll offset at pointerdown — pointer capture doesn't block
   *  wheel scrolling, so the anchor must be re-derived against the CURRENT
   *  scroll on every move to stay pinned to the content rather than the
   *  viewport. */
  startScroll: { x: number; y: number };
  active: boolean;
}

export interface UseMarqueeSelectOptions {
  /** Whether a marquee may start on this pointerdown target (e.g. NOT inside
   *  a card cell — cards own their own gestures). */
  shouldStartOn: (target: Element) => boolean;
  /** Replace the selection with `ids` (called live as the rectangle moves). */
  onMarqueeChange: (ids: Set<string>) => void;
}

export function useMarqueeSelect({
  shouldStartOn,
  onMarqueeChange,
}: UseMarqueeSelectOptions): MarqueeSelect {
  const [marquee, setMarquee] = useState<ActiveMarquee | null>(null);
  const gestureRef = useRef<PendingGesture | null>(null);
  const endedRef = useRef(false);
  // Last emitted selection signature, to skip redundant onMarqueeChange calls.
  const lastEmitRef = useRef<string | null>(null);

  const consumeMarqueeEndClick = useCallback(() => {
    const ended = endedRef.current;
    endedRef.current = false;
    return ended;
  }, []);

  const finish = useCallback((wasActive: boolean) => {
    gestureRef.current = null;
    lastEmitRef.current = null;
    setMarquee(null);
    if (wasActive) {
      // The browser fires a click right after pointerup; the outside-click
      // "clear selection" listener must ignore that one. The timeout is a
      // backstop for gestures that produce no click at all (e.g. cancelled),
      // so the flag can never eat a later, genuine click.
      endedRef.current = true;
      window.setTimeout(() => {
        endedRef.current = false;
      }, 0);
    }
  }, []);

  const gridHandlers = useCallback(
    (sectionId: string): MarqueeGridHandlers => ({
      onPointerDown: (e) => {
        if (e.button !== 0 || !e.isPrimary) {
          return;
        }
        // `isPrimary` is per pointer TYPE, so a primary touch can arrive while
        // a mouse marquee is mid-gesture; replacing that gesture would orphan
        // its rendered rectangle and un-arm its end-click suppression. First
        // gesture wins.
        if (gestureRef.current) {
          return;
        }
        if (!(e.target instanceof Element) || !shouldStartOn(e.target)) {
          return;
        }
        gestureRef.current = {
          sectionId,
          pointerId: e.pointerId,
          grid: e.currentTarget,
          start: { x: e.clientX, y: e.clientY },
          startScroll: { x: window.scrollX, y: window.scrollY },
          active: false,
        };
        // No preventDefault and no capture yet: below the threshold this must
        // stay a plain click (clear-selection / "+" add flows).
      },
      onPointerMove: (e) => {
        const g = gestureRef.current;
        if (!g || e.pointerId !== g.pointerId) {
          return;
        }
        // Primary button no longer held (e.g. released outside the grid
        // before capture was taken): the gesture is over.
        if ((e.buttons & 1) === 0) {
          finish(g.active);
          return;
        }
        const point = { x: e.clientX, y: e.clientY };
        // The anchor corner, pinned to the CONTENT: shift the stored client
        // point by however far the page has scrolled since pointerdown, so
        // mid-drag scrolling doesn't slide the rectangle's origin off the
        // spot the user pressed.
        const anchor = {
          x: g.start.x - (window.scrollX - g.startScroll.x),
          y: g.start.y - (window.scrollY - g.startScroll.y),
        };
        if (!g.active) {
          if (!exceedsThreshold(anchor, point)) {
            return;
          }
          g.active = true;
          // From here the gesture owns the pointer: keep receiving moves
          // outside the grid, and drop any text selection the first few px
          // may have started.
          try {
            g.grid.setPointerCapture(e.pointerId);
          } catch {
            // Synthetic events (tests) have no active pointer to capture.
          }
          window.getSelection()?.removeAllRanges();
        }
        e.preventDefault();
        const clientRect = rectFromPoints(anchor, point);
        const gridBox = g.grid.getBoundingClientRect();
        // Selection: every card of THIS section whose box overlaps the
        // rectangle, live.
        const cards: { id: string; rect: Rect }[] = [];
        for (const el of g.grid.querySelectorAll("[data-card-id]")) {
          const id = (el as HTMLElement).dataset.cardId;
          if (!id) {
            continue;
          }
          const box = el.getBoundingClientRect();
          cards.push({
            id,
            rect: {
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
            },
          });
        }
        const ids = intersectingIds(clientRect, cards);
        const signature = [...ids].sort().join(" ");
        if (signature !== lastEmitRef.current) {
          lastEmitRef.current = signature;
          onMarqueeChange(ids);
        }
        // Draw in grid-local coordinates, clipped to the section's grid.
        const local = clampRectTo(
          {
            left: clientRect.left - gridBox.left,
            top: clientRect.top - gridBox.top,
            width: clientRect.width,
            height: clientRect.height,
          },
          { left: 0, top: 0, width: gridBox.width, height: gridBox.height },
        );
        setMarquee({ sectionId: g.sectionId, rect: local });
      },
      onPointerUp: (e) => {
        const g = gestureRef.current;
        if (!g || e.pointerId !== g.pointerId) {
          return;
        }
        finish(g.active);
      },
      onPointerCancel: (e) => {
        const g = gestureRef.current;
        if (!g || e.pointerId !== g.pointerId) {
          return;
        }
        // A cancelled gesture (e.g. a native drag stole the pointer) never
        // produces a click, so don't arm the click suppression.
        finish(false);
      },
    }),
    [shouldStartOn, onMarqueeChange, finish],
  );

  return { marquee, gridHandlers, consumeMarqueeEndClick };
}
